import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  DeleteCategoryRequest,
  CategoriesResponse,
  RecentCategoriesResponse,
  CategoryFilters,
  CategorySort
} from '@/types/category';
import {
  fetchCategories as apiFetchCategories,
  fetchRecentCategories as apiFetchRecentCategories,
  createCategory as apiCreateCategory,
  updateCategory as apiUpdateCategory,
  deleteCategory as apiDeleteCategory,
  sortCategories,
  filterCategories
} from '@/lib/CategoryManagement';

interface CategoryState {
  categories: Category[];
  recentCategories: Category[];
  filteredCategories: Category[];
  loading: boolean;
  error: string | null;
  filters: CategoryFilters;
  sort: CategorySort;
  selectedCategory: Category | null;
  totalCount: number;
}

const initialState: CategoryState = {
  categories: [],
  recentCategories: [],
  filteredCategories: [],
  loading: false,
  error: null,
  filters: {
    is_active: true,
    search: ''
  },
  sort: {
    field: 'name',
    direction: 'asc'
  },
  selectedCategory: null,
  totalCount: 0
};

// Async thunks
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async ({ coupleId, includeInactive = false }: { coupleId: string; includeInactive?: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiFetchCategories(coupleId, includeInactive);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch categories');
    }
  }
);

export const fetchRecentCategories = createAsyncThunk(
  'categories/fetchRecentCategories',
  async ({ coupleId, limit = 5 }: { coupleId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await apiFetchRecentCategories(coupleId, limit);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch recent categories');
    }
  }
);

export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async (data: CreateCategoryRequest, { rejectWithValue }) => {
    try {
      const category = await apiCreateCategory(data);
      return category;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create category');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async (data: UpdateCategoryRequest, { rejectWithValue }) => {
    try {
      const category = await apiUpdateCategory(data);
      return category;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update category');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (data: DeleteCategoryRequest, { rejectWithValue }) => {
    try {
      await apiDeleteCategory(data);
      return data.id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete category');
    }
  }
);

const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<CategoryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Update filtered categories when filters change
      state.filteredCategories = filterCategories(
        sortCategories(state.categories, state.sort.field, state.sort.direction),
        state.filters.search,
        !state.filters.is_active
      );
    },
    setSort: (state, action: PayloadAction<CategorySort>) => {
      state.sort = action.payload;
      // Update filtered categories when sort changes
      state.filteredCategories = filterCategories(
        sortCategories(state.categories, state.sort.field, state.sort.direction),
        state.filters.search,
        !state.filters.is_active
      );
    },
    setSelectedCategory: (state, action: PayloadAction<Category | null>) => {
      state.selectedCategory = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCategoryState: (state) => {
      state.categories = [];
      state.recentCategories = [];
      state.filteredCategories = [];
      state.selectedCategory = null;
      state.totalCount = 0;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<CategoriesResponse>) => {
        state.loading = false;
        state.categories = action.payload.categories;
        state.totalCount = action.payload.total_count;
        // Update filtered categories
        state.filteredCategories = filterCategories(
          sortCategories(state.categories, state.sort.field, state.sort.direction),
          state.filters.search,
          !state.filters.is_active
        );
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch recent categories
      .addCase(fetchRecentCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentCategories.fulfilled, (state, action: PayloadAction<RecentCategoriesResponse>) => {
        state.loading = false;
        state.recentCategories = action.payload.categories;
      })
      .addCase(fetchRecentCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action: PayloadAction<Category>) => {
        state.loading = false;
        state.categories.push(action.payload);
        state.totalCount += 1;
        // Update filtered categories
        state.filteredCategories = filterCategories(
          sortCategories(state.categories, state.sort.field, state.sort.direction),
          state.filters.search,
          !state.filters.is_active
        );
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action: PayloadAction<Category>) => {
        state.loading = false;
        const index = state.categories.findIndex(cat => cat.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        // Update selected category if it's the one being updated
        if (state.selectedCategory?.id === action.payload.id) {
          state.selectedCategory = action.payload;
        }
        // Update filtered categories
        state.filteredCategories = filterCategories(
          sortCategories(state.categories, state.sort.field, state.sort.direction),
          state.filters.search,
          !state.filters.is_active
        );
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.categories = state.categories.filter(cat => cat.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        // Clear selected category if it's the one being deleted
        if (state.selectedCategory?.id === action.payload) {
          state.selectedCategory = null;
        }
        // Update filtered categories
        state.filteredCategories = filterCategories(
          sortCategories(state.categories, state.sort.field, state.sort.direction),
          state.filters.search,
          !state.filters.is_active
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  setFilters,
  setSort,
  setSelectedCategory,
  clearError,
  resetCategoryState
} = categorySlice.actions;

export default categorySlice.reducer;